import React, { useState } from 'react';
import { Upload, message, Button, Card, Space, Divider, Alert } from 'antd';
import { UploadOutlined, FileOutlined } from '@ant-design/icons';
import { 
  useUploadAvoirsDBF, 
  useUploadAvoirsDetailsDBF, 
  useUploadBothAvoirsDBF 
} from '../../hooks/avoirHooks';

const BulkUploadAvoirs = () => {
  const [avoirsFile, setAvoirsFile] = useState(null);
  const [detailsFile, setDetailsFile] = useState(null);
  
  const { handleUpload: uploadAvoirs, loading: loadingAvoirs, error: errorAvoirs, uploadResult: resultAvoirs } = useUploadAvoirsDBF();
  const { handleUpload: uploadDetails, loading: loadingDetails, error: errorDetails, uploadResult: resultDetails } = useUploadAvoirsDetailsDBF();
  const { handleUpload: uploadBoth, loading: loadingBoth, error: errorBoth, uploadResult: resultBoth } = useUploadBothAvoirsDBF();

  const handleUploadAvoirs = async () => {
    if (!avoirsFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadAvoirs(avoirsFile);
      message.success('Avoirs uploaded successfully!');
      setAvoirsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadDetails = async () => {
    if (!detailsFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadDetails(detailsFile);
      message.success('DetailAvoirs uploaded successfully!');
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadBoth = async () => {
    if (!avoirsFile || !detailsFile) {
      message.error('Please select both DBF files!');
      return;
    }

    try {
      await uploadBoth(avoirsFile, detailsFile);
      message.success('Both files uploaded successfully!');
      setAvoirsFile(null);
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const avoirsProps = {
    onRemove: () => {
      setAvoirsFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setAvoirsFile(file);
      return false;
    },
    fileList: avoirsFile ? [avoirsFile] : [],
    accept: '.dbf,.DBF',
  };

  const detailsProps = {
    onRemove: () => {
      setDetailsFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setDetailsFile(file);
      return false;
    },
    fileList: detailsFile ? [detailsFile] : [],
    accept: '.dbf,.DBF',
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Upload Avoirs DBF Files" style={{ marginBottom: 24 }}>
        <Alert
          message="DBF File Upload Instructions"
          description={
            <div>
              <p><strong>Upload Avoirs Header file (.dbf):</strong> Contains the main avoir records (typically AVRV.dbf)</p>
              <p><strong>Upload Avoirs Detail file (.dbf):</strong> Contains the detail/line items for avoirs (typically DETAVRV.dbf)</p>
              <p><strong>Upload Both:</strong> Upload both files together (recommended)</p>
              <p style={{ marginTop: 8, color: '#52c41a' }}>
                <strong>Note:</strong> Any file name with .dbf or .DBF extension is accepted
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Upload AVRV.dbf */}
          <Card size="small" title="1. Upload Avoirs Headers (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...avoirsProps}>
                <Button icon={<UploadOutlined />} disabled={loadingAvoirs || loadingBoth}>
                  Select DBF File (Headers)
                </Button>
              </Upload>
              {avoirsFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {avoirsFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadAvoirs}
                disabled={!avoirsFile || loadingAvoirs || loadingBoth}
                loading={loadingAvoirs}
              >
                {loadingAvoirs ? 'Uploading...' : 'Upload Avoirs'}
              </Button>
              {errorAvoirs && (
                <Alert message={errorAvoirs} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultAvoirs && (
                <Alert
                  message="Upload Successful"
                  description={resultAvoirs.message || 'Avoirs seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          {/* Upload DETAVRV.dbf */}
          <Card size="small" title="2. Upload Avoirs Details (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...detailsProps}>
                <Button icon={<UploadOutlined />} disabled={loadingDetails || loadingBoth}>
                  Select DBF File (Details)
                </Button>
              </Upload>
              {detailsFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {detailsFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadDetails}
                disabled={!detailsFile || loadingDetails || loadingBoth}
                loading={loadingDetails}
              >
                {loadingDetails ? 'Uploading...' : 'Upload Details'}
              </Button>
              {errorDetails && (
                <Alert message={errorDetails} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultDetails && (
                <Alert
                  message="Upload Successful"
                  description={resultDetails.message || 'DetailAvoirs seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          <Divider>OR</Divider>

          {/* Upload Both */}
          <Card size="small" title="3. Upload Both Files Together (Recommended)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Upload {...avoirsProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Headers File (.dbf)
                  </Button>
                </Upload>
                {avoirsFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {avoirsFile.name}
                  </span>
                )}
              </div>
              <div>
                <Upload {...detailsProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Details File (.dbf)
                  </Button>
                </Upload>
                {detailsFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {detailsFile.name}
                  </span>
                )}
              </div>
              <Button
                type="primary"
                size="large"
                onClick={handleUploadBoth}
                disabled={!avoirsFile || !detailsFile || loadingBoth}
                loading={loadingBoth}
                style={{ marginTop: 16 }}
              >
                {loadingBoth ? 'Uploading Both Files...' : 'Upload Both Files'}
              </Button>
              {errorBoth && (
                <Alert message={errorBoth} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultBoth && (
                <Alert
                  message="Upload Successful"
                  description={resultBoth.message || 'Both files seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
};

export default BulkUploadAvoirs;

